import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, X, Clock } from "lucide-react";
import { createPortal } from "react-dom";
 
export default function LateCompletionModal({ isOpen, onClose, onConfirm, taskTitle }) {
  const [reason, setReason] = useState("");
 
  if (!isOpen) return null;
 
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!reason.trim()) return;
    onConfirm(reason);
    setReason("");
  };
 
  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />
 
        {/* Modal */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative w-full max-w-md bg-[#12141c] border border-white/10 rounded-[2rem] shadow-2xl overflow-hidden shadow-rose-500/10"
        >
          <div className="p-8">
            <div className="flex justify-between items-start mb-6">
              <div className="p-3 bg-rose-500/10 rounded-2xl">
                <Clock className="w-6 h-6 text-rose-500" />
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/5 rounded-full transition-colors text-white/40 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
 
            <h3 className="text-xl font-bold text-white mb-2">Late Completion</h3>
            <p className="text-sm text-white/40 mb-6 leading-relaxed">
              You are completing <span className="text-rose-500 font-medium">"{taskTitle}"</span> after its due date. Please provide a reason for the delay. This will be logged in the journey.
            </p>
 
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest ml-1">
                  Reason for delay
                </label>
                <textarea
                  autoFocus
                  required
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Encountered unexpected technical issues..."
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-sm text-white placeholder-white/20 focus:outline-none focus:border-rose-500/50 focus:ring-1 focus:ring-rose-500/50 transition-all resize-none h-32"
                />
              </div>
 
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-4 rounded-2xl text-sm font-bold text-white/60 hover:bg-white/5 transition-all outline-none"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!reason.trim()}
                  className="flex-1 px-6 py-4 bg-rose-500 hover:bg-rose-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-2xl transition-all shadow-lg shadow-rose-500/20 active:scale-95"
                >
                  Confirm
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}
